import React from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  Stack,
  Skeleton,
  Divider,
} from '@mui/material';
import {
  History,
  UserPlus,
  ArrowRightLeft,
  Play,
  CheckCircle,
  RefreshCw,
  Clock,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import { taskService, type TaskActivity } from '@/services/api/taskService';
import { QUERY_KEYS } from '@/services/queryClient';

dayjs.extend(relativeTime);

function formatDuration(minutes: number) {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
}

function eventIcon(type: string) {
  switch (type) {
    case 'created':
      return <History size={16} />;
    case 'assigned':
      return <UserPlus size={16} />;
    case 'transferred':
      return <ArrowRightLeft size={16} />;
    case 'work_started':
      return <Play size={16} />;
    case 'completed':
      return <CheckCircle size={16} />;
    case 'reopened':
      return <RefreshCw size={16} />;
    case 'status_changed':
      return <RefreshCw size={16} />;
    default:
      return <History size={16} />;
  }
}

function eventColor(type: string) {
  switch (type) {
    case 'completed':
      return 'success.main';
    case 'reopened':
      return 'warning.main';
    case 'transferred':
      return 'warning.main';
    case 'work_started':
      return 'primary.main';
    case 'assigned':
      return 'info.main';
    default:
      return 'text.secondary';
  }
}

interface TaskActivityTabProps {
  taskId: string;
}

const TaskActivityTab: React.FC<TaskActivityTabProps> = ({ taskId }) => {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: QUERY_KEYS.taskActivity(taskId),
    queryFn: () => taskService.getActivity(taskId),
    enabled: !!taskId,
  });

  const activity: TaskActivity = data ?? { events: [], timeByUser: [] };

  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="text" width="40%" />
        <Skeleton variant="rectangular" height={80} sx={{ mt: 1, borderRadius: 1 }} />
        <Skeleton variant="rectangular" height={160} sx={{ mt: 2, borderRadius: 1 }} />
      </Box>
    );
  }

  if (isError) {
    const message = error instanceof Error ? error.message : 'Could not load activity';
    return (
      <Typography variant="body2" color="error">
        {message}
      </Typography>
    );
  }

  return (
    <Box>
      {activity.timeByUser.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
            Time by person
          </Typography>
          <Stack spacing={1}>
            {activity.timeByUser.map((row) => (
              <Box
                key={row.userId}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  px: 1.5,
                  py: 1,
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main', fontSize: 13 }}>
                    {(row.userName || 'U').charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="body2">{row.userName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {row.logCount} {row.logCount === 1 ? 'entry' : 'entries'}
                    </Typography>
                  </Box>
                </Stack>
                <Chip
                  size="small"
                  icon={<Clock size={12} />}
                  label={formatDuration(row.totalMinutes)}
                  variant="outlined"
                />
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
        Timeline
      </Typography>
      {activity.events.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No activity recorded yet.
        </Typography>
      ) : (
        <List disablePadding>
          {activity.events.map((event, index) => (
            <React.Fragment key={`${event.type}-${event.at}-${index}`}>
              <ListItem sx={{ px: 0, alignItems: 'flex-start' }}>
                <ListItemAvatar>
                  <Avatar
                    sx={{
                      bgcolor: 'grey.100',
                      color: eventColor(event.type),
                      width: 36,
                      height: 36,
                    }}
                  >
                    {eventIcon(event.type)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={event.summary}
                  secondary={event.at ? `${dayjs(event.at).format('MMM D, YYYY h:mm A')} · ${dayjs(event.at).fromNow()}` : undefined}
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItem>
              {index < activity.events.length - 1 && <Divider component="li" />}
            </React.Fragment>
          ))}
        </List>
      )}
    </Box>
  );
};

export default TaskActivityTab;
